/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 합계 클래스를 정의한다.
 *
 * @file /src/models/table/Summary.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 8. 11.
 */
import { Table } from '@models/table/Table';
import { Record } from '@models/store/Record';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Summary.css';

export interface Summarizer<T> {
    type: 'sum' | 'count' | 'average' | ((records: Record[], table: T, $column: Dom) => number);
    dataIndex?: string;
    textAlign?: 'left' | 'center' | 'right';
    textClass?: string;
    renderer?: (value: number | string) => string;
}

export class Summary {
    /**
     * 테이블 객체
     */
    table: Table;

    /**
     * 합계행 DOM
     */
    $summary: Dom = null;

    /**
     * 합계행 내부의 고정열 DOM
     */
    $freeze: Dom = null;

    /**
     * 그룹 또는 테이블의 합계행 클래스를 정의한다.
     *
     * @param table - 테이블
     */
    constructor(table: Table) {
        this.table = table;
    }

    /**
     * 테이블을 가져온다.
     *
     * @returns 테이블
     */
    getTable(): Table {
        return this.table;
    }

    /**
     * summary 레이아웃 Dom을 가져온다.
     *
     * @param records - 합계에 포함할 레코드
     * @returns summary 레이아웃 Dom
     */
    $getSummary(records: Record[]): Dom {
        const columns = this.getTable().getColumns();

        if (this.$summary === null) {
            this.$summary = Html.create('div', { 'data-model': 'table', 'data-role': 'summary' });
            this.$summary.append(this.$getFreeze());

            if (this.getTable().getSelection().isMultiple() == true) {
                const $check = Html.create('div', { 'data-model': 'table', 'data-role': 'column', 'data-index': '-1' });
                this.$getFreeze().append($check);
            }

            columns.forEach((column) => {
                if (column.isHidden() == true) return;

                const $column = Html.create('div', {
                    'data-model': 'table',
                    'data-role': 'column',
                    'data-index': column.columnIndex.toString(),
                    'data-view': 'view',
                });

                if (column.summary !== null) {
                    $column.addClass(column.summary.textAlign);
                    if (column.summary.textClass !== null) {
                        $column.addClass(...column.summary.textClass.split(' '));
                    }
                }

                if (column.freeze == true) {
                    this.$getFreeze().append($column);
                } else {
                    this.$summary.append($column);
                }
            });
        }

        /**
         * 합계행이 재호출될 때, 레코드셋이 변경되었을 수 있으므로, 합계행 데이터를 갱신한다.
         */
        columns.forEach((column) => {
            if (column.summary !== null) {
                const $column = Html.get(
                    'div[data-model=table][data-role=column][data-index="' + column.columnIndex + '"]',
                    this.$summary
                );
                let value = null;

                if (column.summary.type instanceof Function) {
                    value = column.summary.type(records, this.table, $column);
                } else {
                    records.forEach((record, index) => {
                        if (column.summary.type === 'sum') {
                            value += record.get(column.summary.dataIndex) ?? 0;
                        }

                        if (column.summary.type === 'count') {
                            value++;
                        }

                        if (column.summary.type === 'average') {
                            value += record.get(column.summary.dataIndex) ?? 0;

                            if (records.length > 0 && index === records.length - 1) {
                                value = value / records.length;
                            }
                        }
                    });
                }

                if (column.summary.renderer !== null) {
                    $column.html(column.summary.renderer(value));
                } else {
                    $column.html((value ?? '').toString());
                }
            }
        });

        return this.$summary;
    }

    /**
     * 고정된 열이 들어가는 Dom 을 가져온다.
     *
     * @returns 고정된 열 Dom
     */
    $getFreeze(): Dom {
        if (this.$freeze === null) {
            this.$freeze = Html.create('div', { 'data-model': 'table', 'data-role': 'freeze' });
        }

        return this.$freeze;
    }

    /**
     * 합계 내 컬럼을 고정한다.
     */
    setFreeze(): void {
        if (this.$summary === null) {
            return;
        }

        Html.all('div[data-model=table][data-role=column]', this.$summary).forEach(($column) => {
            const index = parseInt($column.getAttr('data-index'), 10);
            if (index == -1) {
                this.$getFreeze().append($column);
            } else {
                const column = this.getTable().getColumnAt(index);
                if (column.freeze == true) {
                    this.$getFreeze().append($column);
                } else {
                    this.$summary.append($column);
                }
            }
        });
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.$summary = null;
        this.$freeze = null;
        this.table = null;
    }
}
